import { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Route } from '~/Constants/Routes.constants';
import { Segment } from '~/Helpers/Segment.helper';
import { routeReplacer } from '~/Helpers/String.helper';
import { useNavigation } from '~/Hooks';
import { useRequest } from '~/Hooks/useRequest';
import { BookingStatus, IBookingSimple, PresenceType } from '~/Models/DTO/Booking.models';
import { selectCustomerList } from '~/store/customers';
import { getCustomerOrderLines } from './APIs';
import { useRequestEventProductsForCustomer } from '~/requests';

export interface UseBookingListItemProps {
  booking: IBookingSimple;
  selectable: boolean;
  markPresence: (booking: IBookingSimple, type: PresenceType) => void;
  updateBookingStatus: (booking: IBookingSimple, status: BookingStatus) => void;
  onSelected: (uuid: string) => void;
}

export const useBookingListItem = ({
  booking,
  markPresence,
  selectable,
  onSelected,
  updateBookingStatus,
}: UseBookingListItemProps) => {
  const { navigate, getParams } = useNavigation();
  const { requestWithCallback } = useRequest();
  const { domain } = getParams();
  const { t } = useTranslation();

  const [remainingCredits, setRemainingCredits] = useState('');
  const [productType, setProductType] = useState('unset');
  const [paymentStatus, setPaymentStatus] = useState('unset');
  const Tags = useSelector(selectCustomerList)?.filter(
    (customer) => customer.id === booking.customer.id,
  )?.[0]?.tags;

  const { data: customerProducts } = useRequestEventProductsForCustomer({
    customerId: booking.customer.id?.toString() || '',
    eventId: booking?.event?.id?.toString() || '',
  });

  useEffect(() => {
    getCustomerOrderLines(
      requestWithCallback,
      domain || '',
      booking.customer.id?.toString() || '',
      (orderLines) => {
        const order = orderLines.filter(
          (orderLine) => orderLine.uuid === booking?.orderLine?.uuid,
        )[0];

        if (order?.type) {
          setProductType(order?.type);
        }
        if (order?.paymentStatus) {
          setPaymentStatus(order.paymentStatus);
        }
        if (order?.creditsTotal > 0) {
          setRemainingCredits(
            ` (${order.creditsLeft} / ${order.creditsTotal} ${t('booking.creditsLeft')})`,
          );
        }
      },
      (error) => {
        console.error(error);
      },
    );
  }, [booking.customer.id?.toString()]);

  useEffect(() => {
    const record = customerProducts?.find(({ uuid }) => uuid === booking?.orderLine?.uuid);
    if (record?.type) {
      setProductType(record?.type);
    }
  }, [booking.customer.id?.toString()]);

  const labelId = `bookinglist-item-${booking.uuid}`;

  const handleActionButton = () => {
    if (
      booking.status === BookingStatus.CANCELLED ||
      booking.status === BookingStatus.WAITING_LIST
    ) {
      handleBookingUpdate();
    } else {
      markPresence(booking, PresenceType.PRESENT);
    }
  };

  const handleMarkNeutral = (event: MouseEvent) => {
    event.stopPropagation();
    markPresence(booking, PresenceType.NONE);
  };

  const handleSelect = (event: MouseEvent) => {
    event.preventDefault();
    if (selectable) {
      onSelected(booking.uuid);
    } else {
      Segment.track('click_on_event_go_to_customer_page');
      navigate(
        routeReplacer(Route.CUSTOMER_PROFILE, {
          domain: domain || '',
          customerId: booking?.customer?.uuid ?? '',
        }),
      );
    }
  };

  const handleAddProduct = () => {
    navigate(
      routeReplacer(Route.CUSTOMER_ADD_PRODUCT, {
        domain: domain || '',
        id: booking.event.id,
        customerId: booking.customer.uuid ?? '',
      }),
    );
  };

  const handleBookingUpdate = () => {
    if (
      booking.status === BookingStatus.CANCELLED ||
      booking.status === BookingStatus.WAITING_LIST
    ) {
      updateBookingStatus(booking, BookingStatus.BOOKED);
    } else {
      markPresence(booking, PresenceType.NONE);
      updateBookingStatus(booking, BookingStatus.CANCELLED);
    }
  };

  return {
    handleSelect,
    labelId,
    handleMarkNeutral,
    handleActionButton,
    handleAddProduct,
    handleBookingUpdate,
    productType: productType || booking?.orderLine?.type,
    remainingCredits,
    Tags,
    paymentStatus,
  };
};
